import { ImageOff } from 'lucide-react'
import { type KeyboardEvent, type PointerEvent, useEffect, useState } from 'react'
import type { Background } from '@/api/types/background'
import { Badge } from '@components/ui/badge'
import { Skeleton } from '@components/ui/skeleton'
import { Popover, PopoverAnchor, PopoverContent } from '@components/ui/popover'
import { usePhotoTrayHoverPreview } from '@/hooks/use-photo-tray-hover-preview'
import { useBackgroundPlacementActionsOptional } from '@/contexts/background-placement-context'
import { resolveBackgroundVisual } from '@/lib/backgrounds/resolve-background-visual'
import {
  PHOTO_TRAY_PREVIEW_ALIGN_OFFSET,
  PHOTO_TRAY_PREVIEW_IMAGE_HEIGHT,
  PHOTO_TRAY_PREVIEW_IMAGE_WIDTH,
} from '@/lib/photos/photo-tray-hover-preview'
import { preloadTrayPreviewImage } from '@/lib/photos/preload-tray-preview-image'
import { trayThumbnailBoxClassName } from '@/lib/tray/tray-thumbnail-box'
import { TrayPlacedBadge } from '@components/app/panels/right-panel/tray'
import { cn } from '@/utils'

type BackgroundThumbnailProps = {
  background: Background
  showFileName?: boolean
  /** 1 = single wide row; 2 = compact two-column grid — mirrors PhotoTrayThumbnail. */
  columns: 1 | 2
  /** Reports the hover/focus pop-out preview opening and closing, so the host panel's Escape
   *  chain can dismiss the preview before closing the panel. Reports `false` on unmount if the
   *  preview was open. */
  onPreviewOpenChange?: (open: boolean) => void
}

function BrokenImagePlaceholder() {
  return (
    <div
      className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground"
      role="img"
      aria-label="Broken image"
    >
      <ImageOff className="size-6" aria-hidden="true" />
    </div>
  )
}

/**
 * Backgrounds equivalent of `PhotoTrayThumbnail` — shares its hover/focus pop-out preview
 * mechanics (`usePhotoTrayHoverPreview`, `lib/photos/photo-tray-hover-preview.ts`, both fully
 * generic already) and its pointer-drag-to-place pattern (`BackgroundPlacementContext` mirrors
 * `PhotoPlacementContext`), but drops the photo-only fill-shape/remove affordances, which don't
 * apply to a page/spread background. Shows the same "Placed" badge as Photos when the library
 * response's `placedFlag` says the background is used in the book.
 */
export function BackgroundThumbnail({
  background,
  showFileName = false,
  columns,
  onPreviewOpenChange,
}: BackgroundThumbnailProps) {
  const visual = resolveBackgroundVisual(background)
  const previewEnabled = columns === 2
  const thumbnailSizeClassName = trayThumbnailBoxClassName(columns)

  const [imageFailed, setImageFailed] = useState(false)
  const [previewFailed, setPreviewFailed] = useState(false)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  useEffect(() => {
    setImageFailed(false)
    setPreviewFailed(false)
    setIsImageLoaded(false)
  }, [background.backgroundId])

  const {
    open: previewOpen,
    onTriggerPointerEnter,
    onTriggerPointerLeave,
    onTriggerFocus,
    onTriggerBlur,
    onContentPointerEnter,
    onContentPointerLeave,
    closeNow: closePreview,
    // The single-column row is already wide enough to read the background, so the pop-out
    // preview only earns its place in the compact two-column grid.
  } = usePhotoTrayHoverPreview(previewEnabled)

  useEffect(() => {
    if (!previewOpen) return

    onPreviewOpenChange?.(true)
    return () => onPreviewOpenChange?.(false)
  }, [onPreviewOpenChange, previewOpen])

  const placement = useBackgroundPlacementActionsOptional()

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button > 0) return
    closePreview()
    if (!placement) return

    const rect = event.currentTarget.getBoundingClientRect()

    if (rect.width <= 0 || rect.height <= 0) return

    event.preventDefault()
    placement.beginPointerDrag(background, event.clientX, event.clientY, event.pointerId, rect)
  }

  // Enter/Space "activates the same way as a click" (WMPCM-10936-pattern AC) — there is no click
  // action on this control (placement is a drag gesture), so the keyboard equivalent places the
  // background directly: `activateViaKeyboard` auto-targets the left page, then the right, for a
  // single-page background, and the whole spread for a double-page one, then opens the same tint
  // confirmation a pointer drop does.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    if (!placement) return

    event.preventDefault()
    closePreview()
    placement.activateViaKeyboard(background)
  }

  // Screen resolution is already sharp at the pop-out's 275×188 and a fraction of the print
  // asset's size, which is what made the preview slow to appear; print is only a fallback.
  const previewUrl = background.urlScr || background.urlPri

  // Start the download while the open delay is still running, so the image is usually cached
  // (and decoded) by the time the preview mounts it. Same gating as the preview itself: never for
  // touch, a solid-color swatch, or the single-column layout that has no preview.
  const preloadPreview = () => {
    if (previewEnabled && visual.kind !== 'color') preloadTrayPreviewImage(previewUrl)
  }

  return (
    <Badge className="flex h-auto w-full flex-col items-stretch gap-1 rounded-lg border border-border bg-card p-1 shadow-none hover:bg-card">
      <Popover open={previewOpen} modal={false}>
        <PopoverAnchor asChild>
          <div
            role="img"
            aria-label={`${background.displayName}${background.placedFlag ? ', placed' : ''}. Press Enter or Space to place on canvas.`}
            tabIndex={0}
            className={cn(
              'relative isolate w-full overflow-hidden rounded-md',
              'cursor-grab active:cursor-grabbing',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              thumbnailSizeClassName
            )}
            onPointerEnter={(event) => {
              if (event.pointerType !== 'touch') preloadPreview()
              onTriggerPointerEnter(event.pointerType)
            }}
            onPointerLeave={onTriggerPointerLeave}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              preloadPreview()
              onTriggerFocus()
            }}
            onBlur={onTriggerBlur}
          >
            {visual.kind === 'image' && !imageFailed ? (
              <>
                {!isImageLoaded && (
                  <Skeleton className="absolute inset-0 h-full w-full rounded-md motion-reduce:animate-none" />
                )}
                <img
                  src={visual.url}
                  alt={background.displayName}
                  loading="lazy"
                  decoding="async"
                  className={cn(
                    'absolute inset-0 h-full w-full object-cover',
                    !isImageLoaded && 'opacity-0'
                  )}
                  draggable={false}
                  onLoad={() => setIsImageLoaded(true)}
                  onError={() => setImageFailed(true)}
                />
              </>
            ) : visual.kind === 'color' ? (
              // Formula-color background: no image asset, just a solid-color swatch — see
              // resolveBackgroundVisual (mirrors legacy's odctrl.js swatch <div>).
              <div className="absolute inset-0" style={{ backgroundColor: visual.hex }} />
            ) : (
              <BrokenImagePlaceholder />
            )}
            {background.placedFlag ? <TrayPlacedBadge /> : null}
          </div>
        </PopoverAnchor>
        <PopoverContent
          side="left"
          align="center"
          alignOffset={PHOTO_TRAY_PREVIEW_ALIGN_OFFSET}
          sideOffset={0}
          avoidCollisions
          collisionPadding={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            event.preventDefault()
            closePreview()
          }}
          onPointerEnter={onContentPointerEnter}
          onPointerLeave={onContentPointerLeave}
          onPointerDown={(event) => event.stopPropagation()}
          className="z-[60] w-auto p-4"
          aria-label={`Preview of ${background.displayName}`}
        >
          <div
            className="relative overflow-hidden rounded-md"
            style={{
              width: PHOTO_TRAY_PREVIEW_IMAGE_WIDTH,
              height: PHOTO_TRAY_PREVIEW_IMAGE_HEIGHT,
            }}
          >
            {visual.kind === 'color' ? (
              <div className="absolute inset-0" style={{ backgroundColor: visual.hex }} />
            ) : previewUrl && !previewFailed ? (
              <img
                src={previewUrl}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
                onError={() => setPreviewFailed(true)}
              />
            ) : (
              <BrokenImagePlaceholder />
            )}
          </div>
        </PopoverContent>
      </Popover>
      {showFileName ? (
        <p
          className={cn(
            'w-full truncate px-1 text-left font-normal text-foreground',
            columns === 1 ? 'text-sm leading-5' : 'text-xs leading-4'
          )}
          title={background.displayName}
        >
          {background.displayName}
        </p>
      ) : null}
    </Badge>
  )
}
